import { FormConfig } from "./formRenderer";

export const NMHS_AMBULANCE_MODE_OF_ARRIVAL_CODE = "AM";

// Configuration that matches the current addPatient layout
export const TRIAGE_STANDARD_CONFIG: FormConfig = {
  id: "triage.standard.v1",
  version: 3,
  columns: [
    // Full-width column with all sections
    {
      id: "main-column",
      xs: 12,
      sections: [
        {
          id: "patient-details",
          title: "Patient details",
          headerFields: [{ id: "existingPatientLinkChip" }],
          groups: [
            {
              id: "patient-type",
              fields: [
                { id: "registerAs", label: "Register patient as" }
              ]
            },
            {
              id: "patient-row1",
              layout: "horizontal",
              fields: [
                {
                  id: "lastName",
                  label: "Last name",
                  required: true,
                  disableWhen: { field: "submissionType", value: "unknown" },
                  clearWhen: { field: "submissionType", value: "unknown" }
                },
                {
                  id: "firstName",
                  label: "First name",
                  required: true,
                  disableWhen: { field: "submissionType", value: "unknown" },
                  clearWhen: { field: "submissionType", value: "unknown" }
                },
                { id: "sexAtBirth", label: "Sex at birth" },
                { id: "gender", label: "Gender" },
                { id: "dateOfBirth", label: "Date of birth", placeholder: "DD/MM/YYYY" }
              ]
            },
            {
              id: "patient-status",
              fields: [
                { id: "patientStatusWidget" }
              ]
            },
            {
              id: "patient-row2",
              layout: "horizontal",
              fields: [
                { id: "medicareNumber", label: "Medicare Number", helpText: "Optional" },
                { id: "medicarePosition", label: "Position" },
                { id: "umrn", label: "UMRN", helpText: "Hospital Unit Medical Record Number" },
                { id: "phone", label: "Phone" },
                { id: "email", label: "Email" }
              ]
            },
            {
              id: "patient-previous-names",
              fields: [
                { id: "previousNames", label: "Previous names" }
              ]
            }
          ]
        },
        {
          id: "gp-details",
          title: "GP Details",
          groups: [
            {
              id: "gp-core",
              fields: [
                { id: "gpClinicName", label: "Clinic name" }
              ]
            },
            {
              id: "gp-name",
              layout: "horizontal",
              fields: [
                { id: "gpDoctorLastName", label: "Doctor last name" },
                { id: "gpDoctorFirstName", label: "Doctor first name" }
              ]
            }
          ]
        },
        {
          id: "next-of-kin",
          title: "Next of Kin",
          groups: [
            {
              id: "nok-name",
              layout: "horizontal",
              fields: [
                { id: "nextOfKinLastName", label: "Last name" },
                { id: "nextOfKinFirstName", label: "First name" }
              ]
            },
            {
              id: "nok-relationship",
              fields: [
                { id: "nextOfKinRelationship", label: "Relationship" }
              ]
            }
          ]
        },
        {
          id: "delegate-details",
          title: "Accompanying Person",
          groups: [
            {
              id: "delegate-core",
              fields: [
                { id: "hasDelegatePresent", label: "Accompanying person?" },
                {
                  id: "delegateLastName",
                  label: "Delegate last name",
                  requiredWhen: { field: "delegateCanAct", value: true },
                  showWhen: { field: "hasDelegatePresent", value: true }
                },
                {
                  id: "delegateFirstName",
                  label: "Delegate first name",
                  requiredWhen: { field: "delegateCanAct", value: true },
                  showWhen: { field: "hasDelegatePresent", value: true }
                },
                {
                  id: "delegateRelationship",
                  label: "Relationship to patient",
                  placeholder: "e.g., spouse, parent, caregiver",
                  showWhen: { field: "hasDelegatePresent", value: true }
                },
                {
                  id: "delegateCanAct",
                  label: "Can act on behalf of patient",
                  showWhen: { field: "hasDelegatePresent", value: true },
                  clearWhen: { field: "hasDelegatePresent", value: false }
                },
                {
                  id: "delegatePhone",
                  label: "Delegate phone",
                  showWhen: { field: "delegateCanAct", value: true },
                  clearWhen: { field: "delegateCanAct", value: false }
                },
                {
                  id: "delegateEmail",
                  label: "Delegate email",
                  showWhen: { field: "delegateCanAct", value: true },
                  clearWhen: { field: "delegateCanAct", value: false }
                }
              ]
            }
          ]
        },
        {
          id: "assessment",
          title: "Assessment",
          groups: [
            {
              id: "assessment-core",
              fields: [
                { id: "patientComplaint", label: "Patient complaint", required: true, disabled: true },

                { id: "presentingComplaint", label: "Presenting complaint (specify details)", required: true },
                { id: "complaintCode", label: "Complaint Code" },
                { id: "triageCategory", label: "Triage category" },
                { id: "nursingAssessment", label: "Nursing assessment" }
              ]
            }
          ]
        },
        {
          id: "visit-details",
          title: "Visit details",
          groups: [
            {
              id: "visit-core",
              fields: [
                { id: "typeOfVisit", label: "Type of Visit" },
                { id: "modeOfArrival", label: "Mode of arrival" },
                { id: "location", label: "Location" },
                { id: "ambulanceCaseNumber", label: "Ambulance case number" }
              ]
            }
          ]
        },
        {
          id: "additional-info",
          title: "Additional information",
          groups: [
            {
              id: "additional-core",
              fields: [
                { id: "painScore", label: "Pain score" },
                { id: "frailty", label: "Frailty" },
                { id: "infectiousSymptoms", label: "Patient infectious symptoms" }
              ]
            },
            {
              id: "allergies-fieldset",
              layout: "fieldset",
              title: "Allergies",
              fields: [
                { id: "noAllergies", label: "Patient has no known allergies" },
                {
                  id: "allergies",
                  label: "Allergies",
                  helpText: "Include severity and type",
                  showWhen: { field: "noAllergies", value: false }
                }
              ]
            },
            {
              id: "additional-flags",
              fields: [
                { id: "relatedAlcoholDrugs", label: "Alcohol, Drugs" },
                { id: "fdvRelated", label: "FDV" },
                { id: "representIn48h", label: "Represent in 48h" },
                { id: "injuries", label: "Injuries" }
              ]
            }
          ]
        },
        {
          id: "tasks",
          title: "Tasks",
          groups: [
            {
              id: "tasks-core",
              fields: [
                { id: "tasks" }
              ]
            }
          ]
        }
      ]
    }
  ]
};

// Three-column layout configuration (alternative view)
export const TRIAGE_NMHS_CONFIG: FormConfig = {
  id: "triage.nmhs.v1",
  version: 2,
  columns: [
    // Column 1: Assessment and Register patient as
    {
      id: "column-1",
      xs: 12,
      md: 4,
      sections: [
        {
          "id": "expects-details",
          title: "Expects",
          display: {
            type:"accordion",
            collapsed: true,
          },
          showWhen: { field: "_resumeExpectedEncounter", value: true },
          groups: [
            {
              id: "visit-expects",
              layout: "flow",
              fields: [
                { id: "admittingSpecialty", label: "Admitting specialty", showWhen: { field: "admittingSpecialty", exists: true } },
                { id: "bedSlipSubmitted", label: "Bed slip submitted / Bed manager aware", showWhen: { field: "bedSlipSubmitted", exists: true } },
                { id: "referrerFrom", label: "Referrer from", showWhen: { field: "referrerFrom", exists: true } },
                { id: "referrerContactName", label: "Referrer contact name", showWhen: { field: "referrerContactName", exists: true } },
                { id: "referrerContactNumber", label: "Referrer contact number", showWhen: { field: "referrerContactNumber", exists: true } },
                { id: "triageDirection", label: "Triage directions", showWhen: { field: "triageDirection", exists: true } },
                { id: "patientOrigin", label: "Patient origin", showWhen: { field: "patientOrigin", exists: true } },
              ]
            }
          ]
        },
        {
          id: "assessment",
          title: "Assessment",
          groups: [
            {
              id: "assessment-expects",
              fields: [
                {
                  id: "expects",
                  label: "Expected complaint",
                  showWhen: { field: "expects", exists: true }
                }
              ]
            },
            {
              id: "assessment-core",
              fields: [
                { id: "patientComplaint", label: "Patient complaint", disabled: true, showWhen: { field: "patientComplaint", exists: true } },
                { id: "presentingComplaint", label: "Presenting complaint (specify details)", required: true, securityEnabled: true },
                { id: "complaintCode", label: "Complaint Code", required: true},
                { id: "triageCategory", label: "ATS score", required: true, securityEnabled: true },
                { id: "nursingAssessment", label: "Nursing assessment" }
              ]
            }
          ]
        },
        {
          id: "register-patient-as",
          title: "Register patient as",
          groups: [
            {
              id: "register-core",
              fields: [
                { id: "registerAs" }
              ]
            }
          ]
        }
      ]
    },

    // Column 2: Patient details and Visit details
    {
      id: "column-2",
      xs: 12,
      md: 4,
      sections: [
        {
          id: "patient-details",
          title: "Patient details",
          headerFields: [{ id: "existingPatientLinkChip" }],
          groups: [
            {
              id: "patient-row1",
              layout: "horizontal",
              fields: [
                {
                  id: "lastName",
                  label: "Surname",
                  required: true,
                  disableWhen: { field: "submissionType", value: "unknown" },
                  clearWhen: { field: "submissionType", value: "unknown" }
                },
                {
                  id: "firstName",
                  label: "First name",
                  required: true,
                  disableWhen: { field: "submissionType", value: "unknown" },
                  clearWhen: { field: "submissionType", value: "unknown" }
                }
              ]
            },
            {
              id: "patient-status",
              fields: [
                { id: "patientStatusWidget" }
              ]
            },
            {
              id: "patient-row2",
              layout: "horizontal",
              fields: [
                { id: "dateOfBirth", label: "Date of birth", placeholder: "DD/MM/YYYY", requiredWhen: { field: "submissionType", notValue: "unknown" } },
                { id: "sexAtBirth", label: "Sex at birth", requiredWhen: { field: "submissionType", notValue: "unknown" } }
              ]
            },
            {
              id: "patient-row3",
              layout: "horizontal",
              fields: [
                { id: "gender", label: "Preferred Gender" },
                { id: "pronouns", label: "Pronouns" }
              ]
            },
            {
              id: "patient-umrn",
              fields: [
                { id: "umrn", label: "UMRN" }
              ]
            },
            {
              id: "patient-phone",
              fields: [
                { id: "phone", label: "Phone number" }
              ]
            }
          ]
        },
        {
          id: "visit-details",
          title: "Visit details",
          display: {
            type:"accordion",
            collapsed: false,
          },
          groups: [
            {
              id: "visit-core",
              fields: [
                { id: "typeOfVisit", label: "Type of visit", required: true },
                { id: "modeOfArrival", label: "Mode of arrival", required: true },
                { id: "ambulanceCaseNumber", label: "Ambulance case number", requiredWhen: { field: "modeOfArrival", value: NMHS_AMBULANCE_MODE_OF_ARRIVAL_CODE } },
                { id: "location", label: "Location", required: true },
                { id: "stream", label: "Stream", required: true }
              ]
            }
          ]
        },
        {
          id: "supporting-patient",
          title: "Supporting patient details",
          display: {
            type:"accordion",
            collapsed: true,
          },
          groups: [{
            id: "patient-row4",
            layout: "horizontal",
            fields: [
              { id: "medicareNumber", label: "Medicare No.", placeholder: "(prepopulated from self-registration)" },
              { id: "medicarePosition", label: "Position" }
            ]
          },
          {
            id: "patient-email",
            fields: [
              { id: "email", label: "Email" }
            ]
          },
          {
            id: "patient-previous-names",
            fields: [
              { id: "previousNames", label: "Previous names" }
            ]
          },{
            id: "gp-fieldset",
            layout: "fieldset",
            title: "GP Details",
            fields: [
              {
                id: "gp-core",
                fields: [
                  { id: "gpClinicName", label: "Clinic name" }
                ]
              },
              {
                id: "gp-name",
                layout: "horizontal",
                fields: [
                  { id: "gpDoctorLastName", label: "Doctor last name" },
                  { id: "gpDoctorFirstName", label: "Doctor first name" }
                ]
              }
            ]
          },{
            id: "nok-fieldset",
            layout: "fieldset",
            title: "Next of Kin",
            fields: [
              {
                id: "nok-name",
                layout: "horizontal",
                fields: [
                  { id: "nextOfKinLastName", label: "Last name" },
                  { id: "nextOfKinFirstName", label: "First name" }
                ]
              },
              {
                id: "nok-relationship",
                fields: [
                  { id: "nextOfKinRelationship", label: "Relationship" }
                ]
              }
            ]
          }]
        }
      ]
    },

    // Column 3: Accompanying Person, Additional information, and Tasks
    {
      id: "column-3",
      xs: 12,
      md: 4,
      sections: [
        {
          id: "accompanying-person",
          title: "Accompanying Person",
          groups: [
            {
              id: "accompanying-toggle",
              fields: [
                {
                  id: "hasDelegatePresent",
                  label: "Accompanying person?",
                  labelOverrides: [
                    {label: "Yes", value: true},
                    {label: "No", value: false},
                  ]
                }
              ]
            },
            {
              id: "accompanying-contact",
              layout: "fieldset",
              title: "Delegate Information",
              showWhen: { field: "hasDelegatePresent", value: true },
              fields: [
                {
                  id: "delegateCanAct",
                  label: "Can act on behalf of patient",
                  showWhen: { field: "hasDelegatePresent", value: true },
                  clearWhen: { field: "hasDelegatePresent", value: false }
                },
                {
                  id: "delegatePhone",
                  label: "Delegate phone",
                  showWhen: { field: "delegateCanAct", value: true },
                  clearWhen: { field: "delegateCanAct", value: false }
                },
                {
                  id: "delegateEmail",
                  label: "Delegate email",
                  showWhen: { field: "delegateCanAct", value: true },
                  clearWhen: { field: "delegateCanAct", value: false }
                }
              ]
            },
            {
              id: "accompanying-details",
              layout: "vertical",
              fields: [
                {
                  id: "delegateLastName",
                  label: "Delegate last name",
                  requiredWhen: { field: "delegateCanAct", value: true },
                  showWhen: { field: "hasDelegatePresent", value: true }
                },
                {
                  id: "delegateFirstName",
                  label: "Delegate first name",
                  requiredWhen: { field: "delegateCanAct", value: true },
                  showWhen: { field: "hasDelegatePresent", value: true }
                },
                {
                  id: "delegateRelationship",
                  label: "Relationship to patient",
                  placeholder: "e.g., spouse, parent, caregiver",
                  showWhen: { field: "hasDelegatePresent", value: true }
                }
              ]
            }
          ]
        },
        {
          id: "additional-info",
          title: "Additional information",
          groups: [
            {
              id: "additional-row1",
              layout: "horizontal",
              fields: [
                { id: "painScore", label: "Pain score" },
                { id: "frailty", label: "Frailty" }
              ]
            },
            {
              id: "infectious-symptoms-group",
              fields: [
                { id: "infectiousSymptoms", label: "Infectious symptoms" }
              ]
            },
            {
              id: "allergies-fieldset",
              layout: "fieldset",
              title: "Allergies",
              fields: [
                { id: "noAllergies", label: "Patient has no known allergies" },
                {
                  id: "allergies",
                  label: "Allergies",
                  showWhen: { field: "noAllergies", value: false }
                }
              ]
            },
            {
              id: "additional-row2",
              layout: "horizontal",
              fields: [
                { id: "relatedAlcoholDrugs", label: "Drug/Alcohol" },
                { id: "fdvRelated", label: "FDV Related" }
              ]
            },
            {
              id: "additional-row3",
              layout: "horizontal",
              fields: [
                { id: "representIn48h", label: "Represent in 48 hrs" },
                { id: "injuries", label: "Injury" }
              ]
            },
            {
              id: "injury-fields",
              layout: "horizontal",
              fields: [
                {
                  id: "cause",
                  label: "Cause",
                  showWhen: { field: "injury", value: true },
                  requiredWhen: { field: "injury", value: true }
                },
                {
                  id: "intent",
                  label: "Intent",
                  showWhen: { field: "injury", value: true },
                  requiredWhen: { field: "injury", value: true }
                }
              ]
            }
          ]
        },
        {
          id: "tasks",
          title: "Tasks",
          groups: [
            {
              id: "tasks-core",
              fields: [
                { id: "tasks" }
              ]
            }
          ]
        }
      ]
    }
  ]
};

// Two-column layout configuration for concierge
export const TRIAGE_CONCIERGE_CONFIG: FormConfig = {
  id: "triage.concierge.v1",
  version: 1,
  columns: [
    // Column 1: Assessment and Patient details
    {
      id: "column-1",
      xs: 12,
      md: 6,
      sections: [
        {
          id: "assessment",
          title: "Assessment",
          groups: [
            {
              id: "assessment-core",
              fields: [
                { id: "patientComplaint", label: "Patient complaint", required: true } ]
            }
          ]
        },
        {
          id: "patient-details",
          title: "Patient details",
          headerFields: [{ id: "existingPatientLinkChip" }],
          groups: [
            {
              id: "patient-row1",
              layout: "horizontal",
              fields: [
                {
                  id: "lastName",
                  label: "Surname",
                  required: true
                },
                {
                  id: "firstName",
                  label: "First name",
                  required: true
                }
              ]
            },
            {
              id: "patient-row2",
              layout: "horizontal",
              fields: [
                { id: "dateOfBirth", label: "Date of birth", placeholder: "DD/MM/YYYY" },
                { id: "sexAtBirth", label: "Sex at birth" }
              ]
            },
            {
              id: "patient-row3",
              layout: "horizontal",
              fields: [
                { id: "gender", label: "Preferred Gender" },
                { id: "pronouns", label: "Pronouns" }
              ]
            },
            {
              id: "patient-phone",
              fields: [
                { id: "phone", label: "Phone number" }
              ]
            },
            {
              id: "patient-email",
              fields: [
                { id: "email", label: "Email" }
              ]
            },
            // {
            //   id: "patient-previous-names",
            //   fields: [
            //     { id: "previousNames", label: "Previous names" }
            //   ]
            // }
          ]
        },
        // {
        //   id: "gp-details",
        //   title: "GP Details",
        //   groups: [
        //     {
        //       id: "gp-core",
        //       fields: [
        //         { id: "gpClinicName", label: "Clinic name" }
        //       ]
        //     },
        //     {
        //       id: "gp-name",
        //       layout: "horizontal",
        //       fields: [
        //         { id: "gpDoctorLastName", label: "Doctor last name" },
        //         { id: "gpDoctorFirstName", label: "Doctor first name" }
        //       ]
        //     }
        //   ]
        // },
        // {
        //   id: "next-of-kin",
        //   title: "Next of Kin",
        //   groups: [
        //     {
        //       id: "nok-name",
        //       layout: "horizontal",
        //       fields: [
        //         { id: "nextOfKinLastName", label: "Last name" },
        //         { id: "nextOfKinFirstName", label: "First name" }
        //       ]
        //     },
        //     {
        //       id: "nok-relationship",
        //       fields: [
        //         { id: "nextOfKinRelationship", label: "Relationship" }
        //       ]
        //     }
        //   ]
        // }
      ]
    },

    // Column 2: Additional information
    {
      id: "column-2",
      xs: 12,
      md: 6,
      sections: [
        {
          id: "additional-info",
          title: "Additional information",
          groups: [
            {
              id: "additional-row1",
              layout: "horizontal",
              fields: [
                { id: "painScore", label: "Pain score" },
                { id: "frailty", label: "Frailty" }
              ]
            },
            {
              id: "infectious-symptoms-group",
              fields: [
                { id: "infectiousSymptoms", label: "Infectious symptoms" }
              ]
            },
            {
              id: "allergies-fieldset",
              layout: "fieldset",
              title: "Allergies",
              fields: [
                { id: "noAllergies", label: "Patient has no known allergies" },
                {
                  id: "allergies",
                  label: "Allergies",
                  showWhen: { field: "noAllergies", value: false }
                }
              ]
            }
          ]
        }
      ]
    }
  ]
};

// Export all available configurations
export const FORM_CONFIGS = {
  TRIAGE_STANDARD_CONFIG,
  TRIAGE_NMHS_CONFIG,
  TRIAGE_CONCIERGE_CONFIG,
} as const;

// Helper function to get config by ID
export const getFormConfig = (configId: string): FormConfig | null => {
  switch (configId) {
    case "standard":
      return TRIAGE_STANDARD_CONFIG;
    case "nmhs":
      return TRIAGE_NMHS_CONFIG;
    case "concierge":
      return TRIAGE_CONCIERGE_CONFIG;
    default:
      return null;
  }
};
